import React from 'react';
import {
    Card,
    Typography,
    Space,
    Button,
    Table,
    message,
    Tooltip,
    Alert
} from 'antd';
import {
    SyncOutlined,
    PlayCircleOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Text, Paragraph } = Typography;

interface WebPLogDto {
    timestamp: string;
    mediaFileId: string;
    fileName: string;
    originalPath: string;
    webpPath: string;
    cmsUpdatesCount: number;
    oleUpdatesCount: number;
    details: string[];
}

export const MediaJobs: React.FC = () => {
    const queryClient = useQueryClient();

    // Query to fetch WebP conversion logs
    const { data: logs = [], isLoading, refetch } = useQuery<WebPLogDto[]>({
        queryKey: ['adminWebPLogs'],
        queryFn: async () => {
            const response = await apiClient.get<WebPLogDto[]>('/admin/media/webp-job/logs');
            return response.data;
        }
    });

    // Mutation to trigger WebP conversion job
    const triggerJobMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post('/admin/media/webp-job/trigger');
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success(data?.message || 'WebP conversion job completed successfully!');
            queryClient.invalidateQueries({ queryKey: ['adminWebPLogs'] });
        },
        onError: (error: any) => {
            console.error('WebP conversion job failed:', error);
            message.error(error?.response?.data?.detail || 'Failed to trigger WebP conversion job.');
        }
    });

    const handleTriggerJob = () => {
        triggerJobMutation.mutate();
    };

    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (text: string) => new Date(text).toLocaleString('tr-TR'),
            sorter: (a: WebPLogDto, b: WebPLogDto) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Dosya Adı',
            dataIndex: 'fileName',
            key: 'fileName',
            render: (text: string) => <Text code>{text}</Text>,
        },
        {
            title: 'Orijinal Yol',
            dataIndex: 'originalPath',
            key: 'originalPath',
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text type="secondary">{text}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'WebP Yol',
            dataIndex: 'webpPath',
            key: 'webpPath',
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text type="success">{text}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'CMS Güncelleme',
            dataIndex: 'cmsUpdatesCount',
            key: 'cmsUpdatesCount',
            align: 'center' as const,
            render: (count: number) => (
                <span style={{ fontWeight: count > 0 ? 'bold' : 'normal' }}>
                    {count}
                </span>
            ),
        },
        {
            title: 'OLE Güncelleme',
            dataIndex: 'oleUpdatesCount',
            key: 'oleUpdatesCount',
            align: 'center' as const,
            render: (count: number) => (
                <span style={{ fontWeight: count > 0 ? 'bold' : 'normal' }}>
                    {count}
                </span>
            ),
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>WebP Görsel Dönüştürücü</Title>
                    <Text type="secondary">Sitedeki görsellerin otomatik WebP dönüşümlerini ve içerik entegrasyonlarını yönetin.</Text>
                </div>
                <Button 
                    icon={<SyncOutlined spin={isLoading} />} 
                    onClick={() => refetch()}
                >
                    Yenile
                </Button>
            </div>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4}>Dönüştürme ve Güncelleme İşlemi</Title>
                    <Paragraph>
                        Bu işlem, sistemde kayıtlı olan ancak henüz WebP formatı oluşturulmamış tüm görselleri (PNG, JPG, JPEG, JFIF vb.) 
                        yüksek performanslı ve sıkıştırılmış WebP formatına çevirir. Dönüşümün ardından, site içeriklerindeki (Sayfa içerikleri, 
                        Slider görsel yolları, Kurslar, Projeler vb.) eski görsel adresleri otomatik olarak yeni WebP yollarıyla güncellenir.
                    </Paragraph>
                    <Alert
                        message="Not: Büyük resim dosyalarının işlenmesi ve veri tabanı güncellemeleri, kütüphanedeki resim sayısına bağlı olarak birkaç saniye sürebilir."
                        type="info"
                        showIcon
                    />
                    <div style={{ marginTop: 10 }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={triggerJobMutation.isPending ? <SyncOutlined spin /> : <PlayCircleOutlined />}
                            onClick={handleTriggerJob}
                            loading={triggerJobMutation.isPending}
                            style={{ height: '48px', borderRadius: '6px' }}
                        >
                            {triggerJobMutation.isPending ? 'WebP Dönüşümü Çalıştırılıyor...' : 'Görselleri Dönüştür ve İçeriği Güncelle'}
                        </Button>
                    </div>
                </Space>
            </Card>

            <Card title={<span><FileTextOutlined /> Dönüşüm ve Değişiklik Günlükleri</span>}>
                <Table
                    dataSource={logs}
                    columns={columns}
                    rowKey={(record) => record.mediaFileId + record.timestamp}
                    loading={isLoading}
                    pagination={{ pageSize: 15 }}
                    locale={{ emptyText: 'Henüz WebP dönüşüm kaydı bulunmamaktadır.' }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ margin: 0, paddingLeft: 24 }}>
                                <Text strong>Güncellenen İçerikler:</Text>
                                {record.details && record.details.length > 0 ? (
                                    <ul style={{ marginTop: 8 }}>
                                        {record.details.map((detail, index) => (
                                            <li key={index}><Text type="secondary">{detail}</Text></li>
                                        ))}
                                    </ul>
                                ) : (
                                    <Paragraph type="secondary" style={{ marginTop: 8, fontStyle: 'italic' }}>
                                        Herhangi bir içerik güncellemesi kaydedilmemiş.
                                    </Paragraph>
                                )}
                            </div>
                        ),
                        rowExpandable: (record) => record.cmsUpdatesCount > 0 || record.oleUpdatesCount > 0,
                    }}
                />
            </Card>
        </Space>
    );
};
